import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/MarketplaceAffiliateCommissionInfluencers.graphql';
import { Query, QuerymarketplaceAffiliateCommissionInfluencersArgs } from '../../__generated__/globalTypes';

export const useMarketplaceAffiliateCommissionInfluencersQuery = (
  options?: QueryHookOptions<Query, QuerymarketplaceAffiliateCommissionInfluencersArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerymarketplaceAffiliateCommissionInfluencersArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useMarketplaceAffiliateCommissionInfluencersLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerymarketplaceAffiliateCommissionInfluencersArgs>
): QueryResult<Query, QuerymarketplaceAffiliateCommissionInfluencersArgs> & {
  getMarketplaceAffiliateCommissionInfluencers: LazyQueryExecFunction<
    Query,
    QuerymarketplaceAffiliateCommissionInfluencersArgs
  >;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getMarketplaceAffiliateCommissionInfluencers, result] = useLazyQuery<
    Query,
    QuerymarketplaceAffiliateCommissionInfluencersArgs
  >(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getMarketplaceAffiliateCommissionInfluencers, ...result };
};

export const useMarketplaceAffiliateCommissionInfluencersPromiseQuery = () => {
  const client = useApolloClient();

  const getMarketplaceAffiliateCommissionInfluencers = useCallback(
    (options?: Omit<QueryOptions<QuerymarketplaceAffiliateCommissionInfluencersArgs, Query>, 'query'>) =>
      client.query<Query, QuerymarketplaceAffiliateCommissionInfluencersArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getMarketplaceAffiliateCommissionInfluencers };
};
// Suspense Query Hooks
export const useMarketplaceAffiliateCommissionInfluencersSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerymarketplaceAffiliateCommissionInfluencersArgs>
) => {
  const result = useSuspenseQuery<Query, QuerymarketplaceAffiliateCommissionInfluencersArgs>(QUERY, {
    ...options
  });

  return result;
};
